import {useState} from "react";
import {useTranslation} from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ConfirmationModal from "@/components/ui/confirm-dialog";
import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {Label} from "@/components/ui/label";
import {Textarea} from "@/components/ui/textarea";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {
  DetailField,
  Loader,
  ModalCreatedFooter,
  PageHeader,
  VehicleDetailField,
} from "@/src/modules/core/components";
import {formatDate, formatTime} from "@/src/modules/core/lib/formatters";
import {getCreatorDisplayName} from "@/src/modules/core/lib/get-creator-display-name";

import {getPermitStatusActionFlagsForDisplay, getPermitStatusBadgePropsForDisplay, getPermitSuspendedAtForDisplay, getPermitSuspendReasonForDisplay} from "../lib/permit-status";
import {
  permanentParkingPermitStatusBodySchema,
  type PermanentParkingPermitStatusBody,
} from "../schemas";
import {usePermanentParkingPermit} from "../hooks/use-permanent-parking-permit";
import {usePermanentParkingPermitStatus} from "../hooks/use-permanent-parking-permit-status";

export function PermitDetailModal({
  open,
  permitId,
  onOpenChange,
  hideStatusActions = false,
}: {
  open: boolean;
  permitId: number | null;
  onOpenChange: (open: boolean) => void;
  /** When true, omit suspend / revoke / re-enable controls and header refresh (read-only detail). */
  hideStatusActions?: boolean;
}) {
  const {t} = useTranslation("permanent_parking");
  const {t: tCommon} = useTranslation("common");
  const {permit, isPending, isFetching, refetch} = usePermanentParkingPermit(permitId);
  const [actionDialog, setActionDialog] = useState<{
    type: PermanentParkingPermitStatusBody["type"];
    reason: string;
  } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const {mutateAsync: updateStatus, isPending: isUpdatingStatus} =
    usePermanentParkingPermitStatus();

  const requiresReason =
    actionDialog?.type === "suspend" || actionDialog?.type === "revoke";
  const canConfirmAction = !requiresReason || actionDialog.reason.trim().length > 0;

  const handleOpenAction = (type: PermanentParkingPermitStatusBody["type"]) => {
    setActionError(null);
    setActionDialog({type, reason: ""});
  };

  const handleConfirmAction = async () => {
    if (!actionDialog || permitId == null || permitId <= 0) return;
    const payload = actionDialog.type === "reactivate"
      ? {type: "reactivate" as const}
      : {type: actionDialog.type, reason: actionDialog.reason};
    const parsed = permanentParkingPermitStatusBodySchema.safeParse(payload);
    if (!parsed.success) {
      setActionError(parsed.error.issues[0]?.message ?? "Invalid action payload");
      return;
    }
    await updateStatus({permitId, body: parsed.data});
    setActionDialog(null);
    setActionError(null);
  };

  const statusBadge = permit ? getPermitStatusBadgePropsForDisplay(permit.status, permit) : null;
  const suspendDisplayReason = permit
    ? getPermitSuspendReasonForDisplay(permit)?.trim()
    : undefined;
  const suspendDisplayAt = permit ? getPermitSuspendedAtForDisplay(permit)?.trim() : undefined;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] gap-0 overflow-y-auto p-6 sm:max-w-[700px]">
          {isPending || !permit ? (
            <DialogHeader>
              <DialogTitle className="sr-only">{tCommon("loading")}</DialogTitle>
              <Loader className="h-[10rem]" />
            </DialogHeader>
          ) : (
            <div className="flex flex-col gap-4">
              <DialogHeader className="gap-1 space-y-0 p-0">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <DialogTitle className="flex flex-wrap items-center gap-2 text-base">
                    <span>Permanent Permit #{permit.id}</span>
                    {statusBadge ? (
                      <Badge variant={statusBadge.variant} className="text-xs font-medium">
                        {statusBadge.label}
                      </Badge>
                    ) : null}
                  </DialogTitle>
                  {permitId != null && permitId > 0 && !hideStatusActions ? (
                    <PageHeader.RefreshButton
                      onRefresh={() => void refetch()}
                      isFetching={isFetching}
                    />
                  ) : null}
                </div>
              </DialogHeader>

              <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
                <VehicleDetailField vehicle={permit.vehicle} fallbackId={permit.vehicle_id} />
                <DetailField label="Created by">
                  {getCreatorDisplayName(permit.created_by)}
                </DetailField>
                <DetailField label="Permit number">
                  {permit.permit_number?.trim() ? (
                    <p className="font-mono tabular-nums">{permit.permit_number.trim()}</p>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </DetailField>
                <DetailField label="Property">
                  {permit.property?.name ?? permit.property_id}
                </DetailField>
                <DetailField label="Resident">
                  {permit.resident?.name ?? permit.resident_id}
                </DetailField>
                <DetailField label="Household">
                  {permit.household?.name ?? permit.household_id}
                </DetailField>
                <DetailField label="Start date">
                  {permit.start_date ? formatDate(permit.start_date) : "—"}
                </DetailField>
                <DetailField label="End date">
                  {permit.end_date ? formatDate(permit.end_date) : "—"}
                </DetailField>
                {suspendDisplayAt ? (
                  <DetailField label="Suspended at">
                    {formatDate(suspendDisplayAt)}
                    {suspendDisplayAt.includes("T") ? ` ${formatTime(suspendDisplayAt)}` : ""}
                  </DetailField>
                ) : null}
                {suspendDisplayReason ? (
                  <DetailField label="Suspended reason" className="sm:col-span-2">
                    {suspendDisplayReason}
                  </DetailField>
                ) : null}
              </div>

              {(() => {
                if (hideStatusActions) return null;
                const {canSuspend, canRevoke, canReactivate} = getPermitStatusActionFlagsForDisplay(
                  permit.status,
                  permit,
                );
                if (!canSuspend && !canRevoke && !canReactivate) return null;
                return (
                  <div className="border-border border-t pt-4">
                    <p className="text-muted-foreground text-xs font-medium">Status actions</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {canSuspend && (
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={isUpdatingStatus}
                                onClick={() => handleOpenAction("suspend")}
                              >
                                Suspend
                              </Button>
                            }
                          />
                          <TooltipContent>Suspend this permit</TooltipContent>
                        </Tooltip>
                      )}
                      {canRevoke && (
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <Button
                                size="sm"
                                variant="destructiveOutline"
                                disabled={isUpdatingStatus}
                                onClick={() => handleOpenAction("revoke")}
                              >
                                Revoke
                              </Button>
                            }
                          />
                          <TooltipContent>Revoke this permit permanently</TooltipContent>
                        </Tooltip>
                      )}
                      {canReactivate && (
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={isUpdatingStatus}
                                onClick={() => handleOpenAction("reactivate")}
                              >
                                Re-enable
                              </Button>
                            }
                          />
                          <TooltipContent>Re-enable this suspended permit</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                );
              })()}

              <ModalCreatedFooter createdAt={permit.created_at} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        open={!hideStatusActions && !!actionDialog}
        title={
          actionDialog?.type === "reactivate"
            ? "Re-enable permit"
            : actionDialog?.type === "revoke"
              ? "Revoke permit"
              : "Suspend permit"
        }
        confirmButtonLabel={
          actionDialog?.type === "reactivate"
            ? "Re-enable"
            : actionDialog?.type === "revoke"
              ? "Revoke"
              : "Suspend"
        }
        confirmButtonClassName={
          actionDialog?.type === "revoke"
            ? "bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            : undefined
        }
        loading={isUpdatingStatus}
        disableConfirmButton={!canConfirmAction}
        onClose={() => {
          setActionDialog(null);
          setActionError(null);
        }}
        onConfirm={handleConfirmAction}
      >
        {actionDialog?.type === "reactivate" ? (
          "This will re-enable the permit and restore active processing."
        ) : (
          <div className="flex flex-col gap-2">
            <Label htmlFor="permit-status-reason">Reason</Label>
            <Textarea
              id="permit-status-reason"
              value={actionDialog?.reason ?? ""}
              onChange={(e) => {
                setActionError(null);
                setActionDialog((prev) => (prev ? {...prev, reason: e.target.value} : prev));
              }}
              placeholder={t("form.enter_reason_placeholder")}
              rows={3}
            />
            {actionError && (
              <p className="text-destructive text-sm">{actionError}</p>
            )}
          </div>
        )}
      </ConfirmationModal>
    </>
  );
}
